'use client'

import {useDeferredValue, useEffect, useMemo, useState} from 'react'
import {usePathname, useSearchParams} from 'next/navigation'
import {Search} from 'lucide-react'
import {withDeadline} from '@/lib/withDeadline'
import {readProductSearchResponse} from './readProductSearchResponse'
import {loadProductDocuments} from './loadProductDocuments'
import {useIntl} from 'react-intl'
import {ProductFacetSidebar} from '@/components/products/ProductFacetSidebar'
import {MobileFacetSheet} from '@/components/products/MobileFacetSheet'
import {LoadMoreButton} from '@/components/products/LoadMoreButton'
import {
  ManualsGrid,
  type ManualsGridItem,
  type ProductMarketLink,
} from '@/components/products/ManualsGrid'
import {ManualsChatWidget} from '@/components/products/ManualsChatWidget'
import {Checkbox} from '@/components/ui/checkbox'
import {ProductFacetSkeleton} from './ProductFacetSkeleton'
import type {
  ProductFilterDefinition,
  ProductFilterOption,
} from '@/components/products/ProductCatalogFilters'
import {isProductDiscontinued, productTypeLabel} from '@/components/products/productDisplay'
import {buildFilterUseEvent, pushDataLayerEvent} from '@/lib/analytics'
import {productCanonicalPathForSite} from '@/lib/productCanonical'
import type {
  CanonicalSettingsDocument,
  ProductDocument,
  ProductManualDocument,
} from '@/types/sanity'
import type {SiteLite} from '@/multisite/types'

type ManualsCatalogClientProps = {
  site: SiteLite
  allSites?: SiteLite[]
  canonicalSettings?: CanonicalSettingsDocument | null
  products: ProductDocument[]
  totalProducts: number
  initialQuery?: string
  initialSelectedBrands: string[]
  initialSelectedMarkets: string[]
  initialSelectedProductTypes: string[]
  initialVisibleCount: number
  defaultVisibleCount: number
  maxVisibleCount: number
  siteBrandKeys: string[]
  siteMarketKeys: string[]
}

type CatalogProductDocument = ProductDocument & {
  documentCount?: number
}

type FilterState = {
  brands: string[]
  markets: string[]
  productTypes: string[]
  excludeDiscontinued: boolean
}

type ManualProductGroup = {
  key: string
  product: ProductDocument
  documents: ProductManualDocument[]
  loadDocuments?: (signal?: AbortSignal) => Promise<ProductManualDocument[]>
  brandKey: string
  brandTitle: string
  marketKeys: string[]
  productTypeKey: string
  productTypeLabel: string
  productDiscontinued: boolean
  searchText: string
  compactSearchText: string
}

type FilterCriteria = {
  brands: Set<string>
  markets: Set<string>
  productTypes: Set<string>
  excludeDiscontinued: boolean
}

const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean)))

const normalizeMarketKey = (value?: string | null) =>
  String(value || '')
    .trim()
    .toUpperCase()

const hasGlobalMarket = (product: ProductDocument) =>
  (product.marketKeys || []).some((marketKey) => normalizeMarketKey(marketKey) === 'GLOBAL')

const marketKeySet = (values: Array<string | undefined | null>) =>
  new Set(values.map((value) => normalizeMarketKey(value)).filter(Boolean))

const siteLabel = (site: SiteLite) =>
  site.siteListLabel || site.title || site.siteName || site.locale || site.key

const productMarketLinks = ({
  product,
  site,
  allSites,
  canonicalSettings,
}: {
  product: ProductDocument
  site: SiteLite
  allSites: SiteLite[]
  canonicalSettings?: CanonicalSettingsDocument | null
}): ProductMarketLink[] => {
  if (!(site.isDefaultSite || site.key === 'default')) return []
  if (hasGlobalMarket(product)) return []

  const productMarkets = marketKeySet(product.marketKeys || [])
  if (!productMarkets.size) return []

  const links = new Map<string, ProductMarketLink>()
  for (const candidate of allSites) {
    if (candidate.isDefaultSite || candidate.key === 'default') continue
    const siteMarkets = marketKeySet(candidate.marketKeys || [])
    const matches = Array.from(productMarkets).some((marketKey) => siteMarkets.has(marketKey))
    if (!matches) continue

    const href = productCanonicalPathForSite(product, candidate, canonicalSettings)
    if (!href || links.has(href)) continue
    links.set(href, {href, label: siteLabel(candidate)})
  }

  return Array.from(links.values()).sort((left, right) =>
    left.label.localeCompare(right.label, site.locale, {sensitivity: 'base'}),
  )
}

const paramsToStringArray = (searchParams: URLSearchParams, key: string) =>
  unique(searchParams.getAll(key))

const normalizeScopeKey = (value?: string | null) =>
  String(value || '')
    .trim()
    .toLowerCase()

const matchesMarketScope = (marketKeys: string[], allowedMarkets: Set<string>) => {
  if (allowedMarkets.size === 0) return true
  if (marketKeys.length === 0) return true
  return marketKeys.some((marketKey) => allowedMarkets.has(marketKey))
}

const filterProductsBySiteScope = (
  products: ProductDocument[],
  siteBrandKeys: string[],
  siteMarketKeys: string[],
) => {
  const allowedBrands = new Set(
    siteBrandKeys.map((value) => normalizeScopeKey(value)).filter(Boolean),
  )
  const allowedMarkets = new Set(
    siteMarketKeys.map((value) => normalizeScopeKey(value)).filter(Boolean),
  )

  return products.filter((product) => {
    const brandKey = normalizeScopeKey(product.brandKey)
    const marketKeys = (product.marketKeys || [])
      .map((marketKey) => normalizeScopeKey(marketKey))
      .filter(Boolean)

    if (allowedBrands.size > 0 && !allowedBrands.has(brandKey)) return false
    if (!matchesMarketScope(marketKeys, allowedMarkets)) return false

    return true
  })
}

const normalizeProductLookupKey = (value?: string | null) =>
  String(value || '')
    .replace(/[^\x20-\x7E]/g, '')
    .trim()
    .toUpperCase()

const stripDocumentsForCatalog = (product: ProductDocument): CatalogProductDocument => {
  const {documents, ...rest} = product
  return {
    ...rest,
    documentCount:
      (product as CatalogProductDocument).documentCount ??
      (Array.isArray(documents) ? documents.length : 0),
  }
}

const catalogProductKey = (product: ProductDocument) =>
  normalizeProductLookupKey(product.productNumber) || product._id || product.title || ''

const searchTextForProduct = (product: ProductDocument) =>
  [
    product.title,
    product.productNumber,
    product.brand?.title,
    product.brandKey,
    product.productType?.title,
    product.productTypeKey,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

const compactSearchText = (value: string) => value.replace(/[^a-z0-9]/g, '')

const marketLabel = (value: string, locale: string) => {
  const normalized = value.toUpperCase()
  if (normalized === 'GLOBAL') return 'Global'
  try {
    const formatter = new Intl.DisplayNames([locale], {type: 'region'})
    return formatter.of(normalized) || normalized
  } catch {
    return normalized
  }
}

const sortFilterOptionsByLabel = (options: ProductFilterOption[], locale?: string) =>
  [...options].sort((left, right) =>
    left.label.localeCompare(right.label, locale, {numeric: true, sensitivity: 'base'}),
  )

const uniqueSorted = (values: Array<string | undefined | null>) =>
  Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort()

const brandSortPriority = (brandKey?: string | null) =>
  normalizeScopeKey(brandKey) === 'jotul' ? 0 : 1

const sortManualProductGroups = (groups: ManualProductGroup[], locale?: string) =>
  [...groups].sort((left, right) => {
    const brandPriority = brandSortPriority(left.brandKey) - brandSortPriority(right.brandKey)
    if (brandPriority !== 0) return brandPriority

    return String(left.product.title || left.product.productNumber || left.key).localeCompare(
      String(right.product.title || right.product.productNumber || right.key),
      locale,
      {numeric: true},
    )
  })

const buildManualProductGroups = (
  products: CatalogProductDocument[],
  locale?: string,
): ManualProductGroup[] =>
  sortManualProductGroups(
    products.flatMap((product) => {
      if (!product.documentCount) return []
      // Only a title placeholder is needed before the drawer fetches full documents.
      const documents = [
        {productTitle: product.title || product.productNumber} as ProductManualDocument,
      ]

      const key = catalogProductKey(product)
      const searchText = searchTextForProduct(product)
      const productTypeKey = product.productTypeKey || product.productType?.key || ''

      return {
        key,
        product,
        documents,
        loadDocuments: (signal) => loadProductDocuments(product.productNumber, signal),
        brandKey: product.brandKey || '',
        brandTitle: product.brand?.title || String(product.brandKey || '').toUpperCase(),
        marketKeys: uniqueSorted(product.marketKeys || []),
        productTypeKey,
        productTypeLabel: productTypeLabel(product.productType, locale) || productTypeKey,
        productDiscontinued: isProductDiscontinued(product),
        searchText,
        compactSearchText: compactSearchText(searchText),
      }
    }),
    locale,
  )

const criteriaFromFilters = (filters: FilterState): FilterCriteria => ({
  brands: new Set(filters.brands),
  markets: new Set(filters.markets),
  productTypes: new Set(filters.productTypes),
  excludeDiscontinued: filters.excludeDiscontinued,
})

const matchesText = (group: ManualProductGroup, query: string) => {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return true
  if (group.searchText.includes(normalized)) return true
  return group.compactSearchText.includes(compactSearchText(normalized))
}

const matchesFilters = (
  group: ManualProductGroup,
  criteria: FilterCriteria,
  query: string,
  skip?: keyof FilterState,
) => {
  if (!matchesText(group, query)) return false

  if (skip !== 'brands' && criteria.brands.size && !criteria.brands.has(group.brandKey)) {
    return false
  }

  if (
    skip !== 'markets' &&
    criteria.markets.size &&
    !matchesMarketScope(group.marketKeys, criteria.markets)
  ) {
    return false
  }

  if (
    skip !== 'productTypes' &&
    criteria.productTypes.size &&
    !criteria.productTypes.has(group.productTypeKey)
  ) {
    return false
  }

  if (skip !== 'excludeDiscontinued' && criteria.excludeDiscontinued && group.productDiscontinued) {
    return false
  }

  return true
}

const buildFacetCounts = (
  groups: ManualProductGroup[],
  options: ProductFilterOption[],
  query: string,
  criteria: FilterCriteria,
  key: keyof FilterState,
) => {
  const optionCounts = new Map(options.map((option) => [option.value, 0]))

  for (const group of groups) {
    if (!matchesFilters(group, criteria, query, key)) continue

    let optionValues: string[] = []
    if (key === 'brands') optionValues = [group.brandKey]
    else if (key === 'productTypes') optionValues = [group.productTypeKey]
    else optionValues = group.marketKeys

    for (const optionValue of optionValues) {
      if (!optionValue) continue
      optionCounts.set(optionValue, (optionCounts.get(optionValue) || 0) + 1)
    }
  }

  return optionCounts
}

const toggleValue = (values: string[], value: string) =>
  values.includes(value) ? values.filter((item) => item !== value) : [...values, value]

// Discontinued label resolved via react-intl at the call site (intl.formatMessage).
// The previous inline switch was a manual i18n bypass — the catalog already has
// `product.discontinued` in en.json; locale catalogs carry the translations.

const visibleCountFromParams = (
  searchParams: URLSearchParams | null,
  initialVisibleCount: number,
  defaultVisibleCount: number,
  maxVisibleCount: number,
) => {
  const fromUrl = Number.parseInt(searchParams?.get('limit') || '', 10)
  if (Number.isFinite(fromUrl) && fromUrl > 0) return Math.min(fromUrl, maxVisibleCount)
  return initialVisibleCount || defaultVisibleCount
}

export function ManualsCatalogClient({
  site,
  allSites = [site],
  canonicalSettings,
  products,
  totalProducts,
  initialQuery = '',
  initialSelectedBrands,
  initialSelectedMarkets,
  initialSelectedProductTypes,
  initialVisibleCount,
  defaultVisibleCount,
  maxVisibleCount,
  siteBrandKeys,
  siteMarketKeys,
}: ManualsCatalogClientProps) {
  const [catalogProducts, setCatalogProducts] = useState<CatalogProductDocument[]>(() =>
    products.map(stripDocumentsForCatalog),
  )
  const [loadedProductCount, setLoadedProductCount] = useState(products.length)
  // An empty initial payload is unconfirmed until the first search completes.
  const [resolvedTotalProducts, setResolvedTotalProducts] = useState(
    totalProducts || (products.length ? 0 : Infinity),
  )
  const [loadFailed, setLoadFailed] = useState(false)
  const scopedProducts = useMemo(
    () => filterProductsBySiteScope(catalogProducts, siteBrandKeys, siteMarketKeys),
    [catalogProducts, siteBrandKeys, siteMarketKeys],
  )
  const productGroups = useMemo(
    () => buildManualProductGroups(scopedProducts, site.locale),
    [scopedProducts, site.locale],
  )
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const intl = useIntl()
  const searchPlaceholder = intl.formatMessage({
    id: 'catalog.searchPlaceholder',
    defaultMessage: 'Search by product name or article number...',
  })
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({})
  const [searchInput, setSearchInput] = useState(initialQuery)
  const [isSearchPending, setIsSearchPending] = useState(false)
  const [optimisticQuery, setOptimisticQuery] = useState<string | null>(null)
  const [optimisticFilters, setOptimisticFilters] = useState<FilterState | null>(null)
  const [visibleCount, setVisibleCount] = useState(() =>
    visibleCountFromParams(searchParams, initialVisibleCount, defaultVisibleCount, maxVisibleCount),
  )
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const hideBrandFilter = siteBrandKeys.length > 0
  const hideMarketFilter = !site.isDefaultSite && siteMarketKeys.length > 0
  const isFacetHydrating = !loadFailed && loadedProductCount < resolvedTotalProducts
  const isBootstrappingProducts = isFacetHydrating && loadedProductCount === 0
  const hideNonGlobalProductLinks = Boolean(site.isDefaultSite || site.key === 'default')
  const filterDocumentsByLocale = !hideNonGlobalProductLinks

  useEffect(() => {
    if (loadFailed || loadedProductCount >= resolvedTotalProducts) return

    const controller = new AbortController()
    const loadNextPage = async () => {
      const params = new URLSearchParams({
        index: 'products',
        catalog: 'manuals',
        limit: '100',
        from: String(loadedProductCount),
        excludeDocuments: '1',
      })
      siteMarketKeys.forEach((value) => params.append('marketKeys', value))

      try {
        const data = await withDeadline(
          async (signal) => {
            const response = await fetch(`/api/search?${params.toString()}`, {
              cache: 'no-store',
              signal,
            })
            return readProductSearchResponse(response)
          },
          10000,
          controller.signal,
        )
        if (controller.signal.aborted) return
        const nextProducts = data.hits as unknown as ProductDocument[]
        const nextTotal = data.total
        if (!nextProducts.length && loadedProductCount < nextTotal) {
          throw new Error()
        }
        setResolvedTotalProducts(nextTotal)
        if (!nextProducts.length) return

        setCatalogProducts((current) => {
          const seen = new Set(current.map((product) => catalogProductKey(product)))
          const merged = [...current]
          for (const product of nextProducts) {
            const key = catalogProductKey(product)
            if (key && seen.has(key)) continue
            if (key) seen.add(key)
            merged.push(stripDocumentsForCatalog(product))
          }
          return merged
        })
        setLoadedProductCount((current) => current + nextProducts.length)
      } catch {
        if (controller.signal.aborted) return
        setLoadFailed(true)
        setIsLoadingMore(false)
      }
    }

    void loadNextPage()

    return () => controller.abort()
  }, [loadedProductCount, resolvedTotalProducts, siteMarketKeys, loadFailed])

  const queryFromUrl = searchParams?.get('q') || initialQuery
  const query = optimisticQuery ?? queryFromUrl
  const urlFilters = useMemo<FilterState>(
    () => ({
      brands: hideBrandFilter
        ? []
        : searchParams
          ? paramsToStringArray(searchParams, 'brand')
          : initialSelectedBrands,
      markets: hideMarketFilter
        ? []
        : searchParams
          ? paramsToStringArray(searchParams, 'market')
          : initialSelectedMarkets,
      productTypes: searchParams
        ? paramsToStringArray(searchParams, 'productType')
        : initialSelectedProductTypes,
      excludeDiscontinued: searchParams?.get('excludeDiscontinued') === '1',
    }),
    [
      hideBrandFilter,
      hideMarketFilter,
      initialSelectedBrands,
      initialSelectedMarkets,
      initialSelectedProductTypes,
      searchParams,
    ],
  )
  const filters = optimisticFilters || urlFilters
  const filterCriteria = useMemo(() => criteriaFromFilters(filters), [filters])
  const deferredQuery = useDeferredValue(query)
  const deferredFilterCriteria = useDeferredValue(filterCriteria)

  useEffect(() => {
    setSearchInput(query)
  }, [query])

  useEffect(() => {
    if (!isSearchPending) return
    const timeoutId = window.setTimeout(() => {
      setIsSearchPending(false)
    }, 2500)
    return () => window.clearTimeout(timeoutId)
  }, [isSearchPending])

  useEffect(() => {
    setIsLoadingMore(false)
  }, [visibleCount])

  const filteredGroups = useMemo(
    () =>
      productGroups.filter((group) => matchesFilters(group, deferredFilterCriteria, deferredQuery)),
    [deferredFilterCriteria, deferredQuery, productGroups],
  )

  const definitions: Array<
    ProductFilterDefinition & {
      counts: Map<string, number>
      values: string[]
      stateKey: keyof FilterState
    }
  > = useMemo(() => {
    const brandOptions = Array.from(
      new Map(
        productGroups
          .filter((group) => group.brandKey)
          .map((group) => [group.brandKey, group.brandTitle]),
      ).entries(),
    )
      .sort((left, right) =>
        left[1].localeCompare(right[1], site.locale, {numeric: true, sensitivity: 'base'}),
      )
      .map(([value, label]) => ({value, label}))

    const marketOptions = sortFilterOptionsByLabel(
      Array.from(new Set(productGroups.flatMap((group) => group.marketKeys)))
        .filter((value): value is string => Boolean(value))
        .map((value) => ({value, label: marketLabel(value, site.locale || 'en-US')})),
      site.locale,
    )

    const productTypeOptions = Array.from(
      new Map(
        productGroups
          .filter((group) => group.productTypeKey)
          .map((group) => [group.productTypeKey, group.productTypeLabel]),
      ).entries(),
    )
      .sort((left, right) =>
        left[1].localeCompare(right[1], site.locale, {numeric: true, sensitivity: 'base'}),
      )
      .map(([value, label]) => ({value, label}))

    const base = [
      ...(!hideBrandFilter
        ? [
            {
              key: 'brands',
              label: intl.formatMessage({
                id: 'catalog.filters.brandsLabel',
                defaultMessage: 'Brands',
              }),
              param: 'brand',
              placeholder: intl.formatMessage({
                id: 'catalog.filters.allBrands',
                defaultMessage: 'All brands',
              }),
              options: brandOptions,
              values: filters.brands,
              stateKey: 'brands' as const,
            },
          ]
        : []),
      ...(!hideMarketFilter
        ? [
            {
              key: 'markets',
              label: intl.formatMessage({
                id: 'catalog.filters.marketsLabel',
                defaultMessage: 'Markets',
              }),
              param: 'market',
              placeholder: intl.formatMessage({
                id: 'catalog.filters.allMarkets',
                defaultMessage: 'All markets',
              }),
              options: marketOptions,
              values: filters.markets,
              stateKey: 'markets' as const,
            },
          ]
        : []),
      {
        key: 'productTypes',
        label: intl.formatMessage({
          id: 'catalog.filters.productTypeLabel',
          defaultMessage: 'Product type',
        }),
        param: 'productType',
        placeholder: intl.formatMessage({
          id: 'catalog.filters.allProductTypes',
          defaultMessage: 'All product types',
        }),
        options: productTypeOptions,
        values: filters.productTypes,
        stateKey: 'productTypes' as const,
      },
    ]

    return base.map((definition) => ({
      ...definition,
      counts: buildFacetCounts(
        productGroups,
        definition.options,
        deferredQuery,
        deferredFilterCriteria,
        definition.stateKey,
      ),
    }))
  }, [
    deferredFilterCriteria,
    filters,
    hideBrandFilter,
    hideMarketFilter,
    intl,
    productGroups,
    deferredQuery,
    site.locale,
  ])

  const updateFilters = (
    next: Partial<FilterState>,
    nextVisibleCount = defaultVisibleCount,
    nextQuery = query,
  ) => {
    const params = new URLSearchParams(searchParams?.toString() || '')
    params.delete('brand')
    params.delete('market')
    params.delete('productType')
    params.delete('locale')
    params.delete('localeKey')
    params.delete('localeKeys')
    params.delete('docLocale')
    params.delete('excludeDiscontinued')
    params.delete('limit')

    const merged: FilterState = {
      brands: next.brands ?? filters.brands,
      markets: next.markets ?? filters.markets,
      productTypes: next.productTypes ?? filters.productTypes,
      excludeDiscontinued: next.excludeDiscontinued ?? filters.excludeDiscontinued,
    }
    setOptimisticFilters(merged)
    setVisibleCount(nextVisibleCount)

    merged.brands.forEach((value) => params.append('brand', value))
    merged.markets.forEach((value) => params.append('market', value))
    merged.productTypes.forEach((value) => params.append('productType', value))
    if (merged.excludeDiscontinued) params.set('excludeDiscontinued', '1')

    if (nextQuery) params.set('q', nextQuery)
    else params.delete('q')

    if (nextVisibleCount > defaultVisibleCount) params.set('limit', String(nextVisibleCount))

    window.history.replaceState(
      window.history.state,
      '',
      params.toString() ? `${pathname}?${params.toString()}` : pathname,
    )
    setIsSearchPending(false)
  }

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams?.toString() || '')
    params.delete('brand')
    params.delete('market')
    params.delete('productType')
    params.delete('locale')
    params.delete('limit')

    params.delete('localeKey')
    params.delete('localeKeys')
    params.delete('docLocale')
    params.delete('excludeDiscontinued')

    setVisibleCount(defaultVisibleCount)
    setOptimisticFilters({
      brands: [],
      markets: [],
      productTypes: [],
      excludeDiscontinued: false,
    })

    window.history.replaceState(
      window.history.state,
      '',
      params.toString() ? `${pathname}?${params.toString()}` : pathname,
    )
  }

  const toggleSection = (key: string) => {
    setCollapsedSections((current) => ({
      ...current,
      [key]: !current[key],
    }))
  }

  const handleToggleOption = (key: string, value: string) => {
    const definition = definitions.find((item) => item.key === key)
    if (!definition) return
    pushDataLayerEvent(
      buildFilterUseEvent({
        filterType: key,
        filterValue: value,
      }),
    )
    updateFilters({
      [definition.stateKey]: toggleValue(definition.values, value),
    } as Partial<FilterState>)
  }

  const groupedCount = filteredGroups.length
  const hasManualFilters =
    Boolean(deferredQuery.trim()) ||
    filters.brands.length > 0 ||
    filters.markets.length > 0 ||
    filters.productTypes.length > 0 ||
    filters.excludeDiscontinued
  const displayGroupedCount = hasManualFilters ? groupedCount : resolvedTotalProducts

  // Bound once so the visible text and the checkbox's accessible name cannot
  // drift apart — they must say the same thing to sighted and screen-reader
  // users alike.
  const excludeDiscontinuedLabel = intl.formatMessage({
    id: 'manuals.filters.excludeDiscontinued',
    defaultMessage: 'Exclude discontinued',
  })

  const visibleItems = useMemo<ManualsGridItem[]>(
    () =>
      filteredGroups.slice(0, visibleCount).map((group) => ({
        key: group.key,
        product: group.product,
        documents: group.documents,
        loadDocuments: group.loadDocuments,
        productLinks: productMarketLinks({
          product: group.product,
          site,
          allSites,
          canonicalSettings,
        }),
      })),
    [allSites, canonicalSettings, filteredGroups, site, visibleCount],
  )
  const hasMore = groupedCount > visibleCount

  return (
    <div className="space-y-8">
      {loadFailed ? (
        <div role="alert" className="rounded-card border border-border p-site-md">
          <p>
            {intl.formatMessage({
              id: 'catalog.searchFailed',
            })}
          </p>
          <button
            type="button"
            className="mt-site-sm underline"
            onClick={() => setLoadFailed(false)}
          >
            {intl.formatMessage({id: 'common.retry', defaultMessage: 'Retry'})}
          </button>
        </div>
      ) : null}
      <form
        onSubmit={(event) => {
          event.preventDefault()
          const nextQuery = searchInput.trim()
          if (nextQuery === query) return
          setOptimisticQuery(nextQuery)
          setIsSearchPending(true)
          updateFilters({}, defaultVisibleCount, nextQuery)
        }}
        className="flex flex-col gap-4 rounded-card border border-border bg-white/90 px-5 py-5 shadow-sm backdrop-blur sm:px-7 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-3 lg:flex-row lg:items-center">
          <p
            id="manuals-search-heading"
            className="shrink-0 text-lg font-semibold tracking-tight text-foreground"
          >
            {intl.formatMessage({id: 'manuals.search.heading', defaultMessage: 'Search manuals'})}
          </p>
          <label className="flex w-full items-center gap-3 rounded-full border border-border bg-white px-5 py-4 shadow-sm">
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={searchPlaceholder}
              aria-labelledby="manuals-search-heading"
              className="min-w-0 flex-1 bg-transparent text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:rounded-sm placeholder:text-muted-foreground"
            />
            <Search className="size-5 text-muted-foreground" />
          </label>
        </div>
        <button
          type="submit"
          disabled={isSearchPending}
          className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-orange-400"
        >
          {isSearchPending
            ? intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading...'})
            : intl.formatMessage({id: 'navigation.search.label', defaultMessage: 'Search'})}
        </button>
        <ManualsChatWidget locale={site.locale} />
        {query || searchInput ? (
          <button
            type="button"
            onClick={() => {
              setSearchInput('')
              setOptimisticQuery('')
              setIsSearchPending(true)
              updateFilters({}, defaultVisibleCount, '')
            }}
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-border hover:bg-muted"
          >
            {intl.formatMessage({id: 'manuals.filters.clear', defaultMessage: 'Clear'})}
          </button>
        ) : null}
      </form>

      <div className="flex gap-site-section-sm">
        {/* Hidden below lg — the same sidebar is reachable there through
            MobileFacetSheet in the results column. */}
        <div className="hidden w-[240px] shrink-0 lg:block">
          {isFacetHydrating || isBootstrappingProducts ? (
            <ProductFacetSkeleton />
          ) : (
            <ProductFacetSidebar
              definitions={definitions}
              collapsedSections={collapsedSections}
              onToggleSection={toggleSection}
              onClearAll={clearFilters}
              onToggleOption={handleToggleOption}
            />
          )}
        </div>

        <div className="flex-1">
          {isFacetHydrating || isBootstrappingProducts ? null : (
            <MobileFacetSheet
              className="mb-site-md"
              definitions={definitions}
              collapsedSections={collapsedSections}
              onToggleSection={toggleSection}
              onClearAll={clearFilters}
              onToggleOption={handleToggleOption}
            />
          )}
          <div className="mb-site-md flex flex-wrap items-center justify-between gap-3">
            <p className="u-body-sm text-muted-foreground">
              {loadFailed && !groupedCount
                ? null
                : isBootstrappingProducts
                  ? intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading...'})
                  : intl.formatMessage(
                      {
                        id: 'catalog.productCount',
                        defaultMessage:
                          '{n, plural, =0 {No products} one {# product} other {# products}}',
                      },
                      {n: displayGroupedCount},
                    )}
            </p>
            <label
              htmlFor="manuals-exclude-discontinued"
              className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground"
            >
              {/*
                The wrapping label is not enough on its own: ui/checkbox renders
                base-ui's `role="checkbox"` span, which is not a labelable
                element, so neither htmlFor nor containment names it. Without
                aria-label a screen reader announces an unnamed checkbox
                (axe: aria-toggle-field-name). Found on production by the
                JO2-204 mobile audit.
              */}
              <Checkbox
                id="manuals-exclude-discontinued"
                aria-label={excludeDiscontinuedLabel}
                checked={filters.excludeDiscontinued}
                onCheckedChange={() => {
                  updateFilters({excludeDiscontinued: !filters.excludeDiscontinued})
                }}
              />
              {excludeDiscontinuedLabel}
            </label>
          </div>

          {visibleItems.length ? (
            <>
              <ManualsGrid
                items={visibleItems}
                locale={site.locale}
                filterDocumentsByLocale={filterDocumentsByLocale}
                discontinuedLabel={intl.formatMessage({
                  id: 'product.discontinued',
                  defaultMessage: 'Product discontinued',
                })}
                resolveProductHref={(product) =>
                  hideNonGlobalProductLinks && !hasGlobalMarket(product)
                    ? ''
                    : productCanonicalPathForSite(product, site, canonicalSettings)
                }
              />
              {hasMore ? (
                <div className="mt-10 flex justify-center">
                  <LoadMoreButton
                    loading={isLoadingMore}
                    onClick={() => {
                      const nextVisibleCount = Math.min(
                        visibleCount + defaultVisibleCount,
                        maxVisibleCount,
                      )
                      setIsLoadingMore(true)
                      updateFilters({}, nextVisibleCount)
                    }}
                  />
                </div>
              ) : null}
            </>
          ) : loadFailed ? null : isBootstrappingProducts ? (
            <div className="rounded-card border border-border bg-white px-8 py-12 text-center shadow-sm">
              <p className="text-lg font-medium text-muted-foreground">
                {intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading...'})}
              </p>
            </div>
          ) : (
            <div className="rounded-card border border-dashed border-border bg-white px-8 py-12 text-center shadow-sm">
              <p className="text-lg font-medium text-foreground">
                {query
                  ? intl.formatMessage({
                      id: 'manuals.empty.search',
                      defaultMessage: 'No manuals match this search yet.',
                    })
                  : intl.formatMessage({
                      id: 'manuals.empty.site',
                      defaultMessage: 'No manuals available yet for this site.',
                    })}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
